/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy meant touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations.*/
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by foreground reminders window.*/
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session).*/
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (even though it currently matches the
 * offline-cache value) because it is an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time ceiling for session check performed by route guards.
 *
 * P23/CC-60 · I1-bis (MEASURED in this repo, 2026-08-02): `beforeLoad` of
 * `_auth` awaited `get-session` WITHOUT ceiling. Defect does not appear on 401 or
 * 5xx — both resolve quickly and `queryFn` converts them into SUCCESS
 * with `null`—: appears when response is STALLED. Router then does not
 * mount route, `<Suspense fallback={null}>` of `__root` has nothing to
 * render and entire app remains BLANK for as long as network takes.
 * Measured on `/login` over PRODUCTION BUNDLE: empty `innerText` at 3,
 * 6, 10, 15 and 20 s.
 *
 * SINGLE source for both guards (`_app` and `_auth`): having number in two
 * local literals was exactly what left `_auth` without the defense that
 * `_app` had, and that is why I1 closed green measuring `/` (healthy) and not
 * `/login` (broken). Past ceiling screen is rendered; late response
 * keeps syncing machine and cache, and `BootManager` redirects after if
 * session was present.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (title and notes in
 * detail, subtasks). Balances typing responsiveness with mutation volume.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant).*/
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably longer than the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
